import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject, debounceTime, distinctUntilChanged, firstValueFrom, takeUntil } from 'rxjs';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { User } from '../../../models/user/User.model';
import {
  LocationDTO,
  LocationInformationDTO,
} from '../../../dto/location/location.dto';
import { environment } from '../../../environment/environment';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserDTO } from '../../../dto/users/user.dto';
import { DunaValidationStatus } from '../../../dto/users/user-status.dto';
import { DunaProfileImageComponent } from '../duna-profile-image/duna-profile-image.component';
import { DunaEducationalPathBadgeComponent } from '../duna-educational-path-badge/duna-educational-path-badge.component';
import { DunaValidationBadgeComponent } from '../duna-validation-badge/duna-validation-badge.component';
import { DunaUserProgressLabelComponent } from '../duna-user-progress-label/duna-user-progress-label.component';
import { buildProgressLabelInputsFromStatus } from '../duna-user-progress-label/duna-user-progress-breakdown.util';
import { ProfileImageService } from '../../../services/profile-image/profile-image.service';
import { ProfileImageDTO } from '../../../dto/profile-image/profile-image.dto';
import { AuthService } from '../../../services/auth/auth.service';
import { UserService } from '../../../services/user/user.service';
import { userCanViewUserGroupsLocation } from '../duna-user-groups-location/user-groups-location-access.utils';
import {
  DirectorySortColumn,
  DirectorySortDirection,
  buildServerLoadParams,
  mapUserListSortToApi,
  toggleDirectorySort,
} from '../duna-directory-list/duna-directory-list.logic';

interface LocationUserRoleLabel {
  type: 'sekundaer' | 'uddansvarlig' | 'uddassistent';
  label: string;
}

interface LocationUserRow {
  user: UserDTO;
  uuid: string;
  initials: string;
  roleLabels: LocationUserRoleLabel[];
  educationalPaths: any[];
  validationStatus: DunaValidationStatus | null;
  registeredPercentage: number;
  progressPercentage: number;
  completedBarWidth: number;
  intendedBarWidth: number;
  progressLabel: ReturnType<typeof buildProgressLabelInputsFromStatus>;
}

const EMPTY_EDUCATIONAL_PATHS: any[] = [];

@Component({
  selector: 'app-duna-location-information',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatPaginatorModule,
    DunaProfileImageComponent,
    DunaEducationalPathBadgeComponent,
    DunaValidationBadgeComponent,
    DunaUserProgressLabelComponent,
  ],
  template: `
    @if (locationInfo) {
    <div class="location-container" [ngClass]="{ 'compact-mode': compactMode }">
      <div class="location-header">
        @if (location) {
        <app-duna-profile-image
          [obfuscatedName]="
            profileImageMeta?.profile_image_obfuscated_name ??
            location.profile_image_obfuscated_name ??
            null
          "
          [fallbackInitials]="getLocationInitials()"
          [size]="96"
          [canEdit]="profileImageMeta?.can_edit_profile_image ?? false"
          [scopeType]="'location'"
          [scopeId]="location.id"
          [entityUuid]="location.guid"
          [entityKind]="'location'"
          (obfuscatedNameChange)="onLocationProfileImageChanged()"
        ></app-duna-profile-image>
        }
        <div class="location-header-main">
          <div class="location-title">
            <h2>{{ locationInfo.locations_name }}</h2>
            @if (locationInfo.address) {
            <p class="location-address">{{ locationInfo.address }}</p>
            }
          </div>
          <div class="location-stats">
            <div class="stat-item">
              <mat-icon>people</mat-icon>
              <span>Brugere: {{ locationInfo.total_people_count }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="location-content">
        <div class="education-team">
          <section class="team-section team-section--responsible">
            <div class="team-section-header">
              <mat-icon>school</mat-icon>
              <h3>Uddannelsesansvarlige</h3>
            </div>
            @if (locationInfo.education_responsibles.length) {
            <div class="team-members-block">
              <div class="team-members">
              @for (manager of visibleEducationResponsibles; track manager.uuid) {
              <div class="team-member">
                <app-duna-profile-image
                  [obfuscatedName]="
                    manager.profile_image_obfuscated_name ?? null
                  "
                  [fallbackInitials]="getUserInitials(manager)"
                  [size]="56"
                  [canEdit]="false"
                  [scopeType]="'user'"
                  [scopeId]="manager.id ?? undefined"
                  [entityUuid]="manager.uuid"
                  [entityKind]="'user'"
                ></app-duna-profile-image>
                <div class="team-member-details">
                  <span class="team-member-name"
                    >{{ manager.first_name }} {{ manager.last_name }}</span
                  >
                  <span class="team-member-role">Uddannelsesansvarlig</span>
                </div>
              </div>
              }
              </div>
              @if (showEducationResponsiblesExpandToggle) {
              <button
                type="button"
                class="team-section-expand team-section-expand--footer team-section-expand--responsible"
                [attr.aria-expanded]="educationResponsiblesExpanded"
                [attr.aria-label]="
                  educationResponsiblesExpanded
                    ? 'Vis færre uddannelsesansvarlige'
                    : 'Vis alle uddannelsesansvarlige'
                "
                (click)="toggleEducationResponsiblesExpanded()"
              >
                @if (educationResponsiblesExpanded) {
                <span>Vis færre</span>
                <mat-icon>expand_less</mat-icon>
                } @else {
                <span
                  >Vis {{ hiddenEducationResponsiblesCount }} flere</span
                >
                <mat-icon>expand_more</mat-icon>
                }
              </button>
              }
            </div>
            } @else {
            <p class="team-empty">Der er ingen uddannelsesansvarlige</p>
            }
          </section>
          <section class="team-section team-section--assistant">
            <div class="team-section-header">
              <mat-icon>support_agent</mat-icon>
              <h3>Uddannelsesassistenter</h3>
            </div>
            @if (locationInfo.education_assistants.length) {
            <div class="team-members-block">
              <div class="team-members">
              @for (assistant of visibleEducationAssistants; track assistant.uuid) {
              <div class="team-member">
                <app-duna-profile-image
                  [obfuscatedName]="
                    assistant.profile_image_obfuscated_name ?? null
                  "
                  [fallbackInitials]="getUserInitials(assistant)"
                  [size]="56"
                  [canEdit]="false"
                  [scopeType]="'user'"
                  [scopeId]="assistant.id ?? undefined"
                  [entityUuid]="assistant.uuid"
                  [entityKind]="'user'"
                ></app-duna-profile-image>
                <div class="team-member-details">
                  <span class="team-member-name"
                    >{{ assistant.first_name }} {{ assistant.last_name }}</span
                  >
                  <span class="team-member-role">Uddannelsesassistent</span>
                </div>
              </div>
              }
              </div>
              @if (showEducationAssistantsExpandToggle) {
              <button
                type="button"
                class="team-section-expand team-section-expand--footer team-section-expand--assistant"
                [attr.aria-expanded]="educationAssistantsExpanded"
                [attr.aria-label]="
                  educationAssistantsExpanded
                    ? 'Vis færre uddannelsesassistenter'
                    : 'Vis alle uddannelsesassistenter'
                "
                (click)="toggleEducationAssistantsExpanded()"
              >
                @if (educationAssistantsExpanded) {
                <span>Vis færre</span>
                <mat-icon>expand_less</mat-icon>
                } @else {
                <span>Vis {{ hiddenEducationAssistantsCount }} flere</span>
                <mat-icon>expand_more</mat-icon>
                }
              </button>
              }
            </div>
            } @else {
            <p class="team-empty">Der er ingen uddannelsesassistenter</p>
            }
          </section>
        </div>
        <section class="team-section team-section--users">
          <div class="team-section-header">
            <mat-icon>groups</mat-icon>
            <h3>Alle brugere</h3>
            @if (usersTotalCount > 0 || usersListLoading) {
            <span class="team-count">{{ getAllUsersCountLabel() }}</span>
            }
          </div>
          <mat-form-field
            appearance="outline"
            class="users-search"
            subscriptSizing="dynamic"
          >
            <mat-label
              >Søg navn, rolle, uddannelsesvej eller DUNA-status</mat-label
            >
            <input
              matInput
              [ngModel]="userSearch"
              (ngModelChange)="onUserSearchChange($event)"
            />
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>
          @if (filteredUserRows.length) {
          <div
            class="all-users-directory"
            [class.all-users-directory--limited]="!canViewUserDetails"
            role="list"
          >
            <div class="all-users-header">
              <span class="all-users-header-avatar"></span>
              <button
                type="button"
                class="all-users-header-sort all-users-header-identity"
                (click)="onUserSortHeaderClick('identity', $event)"
              >
                Navn
                <mat-icon class="sort-icon">{{ userSortIcon('identity') }}</mat-icon>
              </button>
              <button
                type="button"
                class="all-users-header-sort all-users-header-meta"
                (click)="onUserSortHeaderClick('meta', $event)"
              >
                Uddannelse
                <mat-icon class="sort-icon">{{ userSortIcon('meta') }}</mat-icon>
              </button>
              @if (canViewUserDetails) {
              <button
                type="button"
                class="all-users-header-sort all-users-header-stamp"
                (click)="onUserSortHeaderClick('stamp', $event)"
              >
                DUNA
                <mat-icon class="sort-icon">{{ userSortIcon('stamp') }}</mat-icon>
              </button>
              <button
                type="button"
                class="all-users-header-sort all-users-header-progress"
                (click)="onUserSortHeaderClick('progress', $event)"
              >
                Fremgang
                <mat-icon class="sort-icon">{{ userSortIcon('progress') }}</mat-icon>
              </button>
              <span class="all-users-header-action"></span>
              }
            </div>
            <div class="all-users-viewport">
              @for (row of filteredUserRows; track row.uuid) {
              <div
                class="team-member team-member--user"
                role="listitem"
                [class.clickable]="canViewUserDetails"
                (click)="onUserCardClick(row.user)"
              >
                <app-duna-profile-image
                  [obfuscatedName]="
                    row.user.profile_image_obfuscated_name ?? null
                  "
                  [fallbackInitials]="row.initials"
                  [size]="40"
                  [canEdit]="false"
                  [scopeType]="'user'"
                  [scopeId]="row.user.id ?? undefined"
                  [entityUuid]="row.uuid"
                  [entityKind]="'user'"
                ></app-duna-profile-image>
                <div class="team-member-details">
                  <span class="team-member-name"
                    >{{ row.user.first_name }} {{ row.user.last_name }}</span
                  >
                  @if (row.roleLabels.length) {
                  <div class="user-role-labels">
                    @for (roleLabel of row.roleLabels; track roleLabel.type) {
                    <span
                      class="user-role-label"
                      [ngClass]="'user-role-label--' + roleLabel.type"
                      >{{ roleLabel.label }}</span
                    >
                    }
                  </div>
                  }
                </div>
                <div class="user-meta">
                  <app-duna-educational-path-badge
                    [educationalPaths]="row.educationalPaths"
                    [currentYear]="currentYear"
                    [showStatus]="canViewUserDetails"
                  ></app-duna-educational-path-badge>
                </div>
                <div class="user-stamp">
                  @if (canViewUserDetails) {
                    @if (row.validationStatus; as status) {
                    <app-duna-validation-badge
                      [status]="status"
                      [isDetailBadge]="true"
                    ></app-duna-validation-badge>
                    }
                  }
                </div>
                <div class="user-progress-row">
                  @if (canViewUserDetails) {
                  <app-duna-user-progress-label
                    [registeredPercentage]="row.progressLabel.registeredPercentage"
                    [progressPercentage]="row.progressLabel.progressPercentage"
                    [paths]="row.progressLabel.paths"
                    [totals]="row.progressLabel.totals"
                    [year]="currentYear"
                    (click)="$event.stopPropagation()"
                  ></app-duna-user-progress-label>
                  }
                </div>
                @if (canViewUserDetails) {
                <mat-icon class="profile-chevron">chevron_right</mat-icon>
                } @else {
                <span class="all-users-header-action"></span>
                }
              </div>
              }
            </div>
          </div>
          <mat-paginator
            [length]="usersTotalCount"
            [pageIndex]="usersPageIndex"
            [pageSize]="usersPageSize"
            [pageSizeOptions]="usersPageSizeOptions"
            (page)="onUsersPageChange($event)"
            aria-label="Vælg side med brugere"
          ></mat-paginator>
          } @else {
          <p class="team-empty">
            {{
              userSearch
                ? 'Ingen brugere matcher søgningen'
                : 'Der er ingen brugere på lokationen'
            }}
          </p>
          }
        </section>
      </div>
    </div>
    }

    <!-- Error message -->
    @if (error) {
    <div class="error-message">
      {{ error }}
    </div>
    }

    <!-- Loading indicator -->
    @if (!locationInfo && !error) {
    <div class="loading-info">
      <p>Loading location information...</p>
    </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      .location-container {
        display: flex;
        flex-direction: column;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        background-color: var(--surface, white);
        height: auto;
      }

      .location-container.compact-mode .location-header,
      .location-container.compact-mode .location-content {
        padding: 12px;
      }

      .location-container.compact-mode .all-users-header,
      .location-container.compact-mode .team-member--user {
        padding: 8px 0;
      }

      .location-header {
        padding: 16px;
        background-color: var(--surfaceContainerLow, #f5f5f5);
        border-bottom: 1px solid var(--outline, #e0e0e0);
        flex-shrink: 0;
        display: flex;
        align-items: flex-start;
        gap: 16px;
      }

      .location-title {
        margin-bottom: 0;
        flex: 1;
        min-width: 0;
      }

      .location-header-main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .location-title h2 {
        margin: 0 0 4px 0;
        font-size: 20px;
        font-weight: 500;
        color: var(--onSurface, #333);
      }

      .location-address {
        margin: 0;
        color: var(--onSurfaceVariant, #666);
        font-size: 14px;
      }

      .location-stats {
        display: flex;
        flex-wrap: wrap;
      }

      .stat-item {
        display: flex;
        align-items: center;
        margin-right: 24px;
        margin-bottom: 8px;
        color: var(--onSurface, #444);
      }

      .stat-item mat-icon {
        margin-right: 8px;
        color: var(--primary, #305080);
      }

      .location-content {
        padding: 16px;
      }

      .education-team {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
        margin-bottom: 16px;
      }

      .team-section {
        border-radius: 10px;
        padding: 16px;
        background-color: var(--surfaceContainerLow, #fafafa);
        border: 1px solid var(--outline, #e8e8e8);
      }

      .team-section--responsible {
        border-top: 3px solid var(--primary, #305080);
      }

      .team-section--assistant {
        border-top: 3px solid var(--secondary, #5d6b82);
      }

      .team-section--users {
        border-top: 3px solid var(--tertiary, #a54342);
      }

      .team-section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
      }

      .team-section-header mat-icon {
        color: var(--onSurfaceVariant, #666);
        font-size: 22px;
        width: 22px;
        height: 22px;
      }

      .team-section--responsible .team-section-header mat-icon {
        color: var(--primary, #305080);
      }

      .team-section--assistant .team-section-header mat-icon {
        color: var(--secondary, #5d6b82);
      }

      .team-section--users .team-section-header mat-icon {
        color: var(--tertiary, #a54342);
      }

      .team-section-header h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: var(--onSurface, #333);
      }

      .team-count {
        margin-left: auto;
        font-size: 12px;
        font-weight: 600;
        color: var(--onSurfaceVariant, #666);
        background: var(--surface, #fff);
        border-radius: 999px;
        padding: 2px 10px;
      }

      .team-members-block {
        display: flex;
        flex-direction: column;
        gap: 0;
        min-width: 0;
      }

      .team-section-expand {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        border: none;
        cursor: pointer;
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }

      .team-section-expand--footer {
        width: 100%;
        margin-top: 4px;
        padding: 10px 12px;
        border-radius: 8px;
        box-sizing: border-box;
        border: 1px dashed
          color-mix(in srgb, var(--outline, #e0e0e0) 70%, transparent);
        background: var(--surface, white);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        transition:
          background-color 0.15s ease,
          border-color 0.15s ease,
          color 0.15s ease;
      }

      .team-section-expand--responsible {
        color: var(--primary, #305080);
        border-color: color-mix(
          in srgb,
          var(--primary, #305080) 35%,
          var(--outline, #e0e0e0)
        );
        background: color-mix(
          in srgb,
          var(--primary, #305080) 6%,
          var(--surface, white)
        );
      }

      .team-section-expand--responsible:hover {
        background: color-mix(
          in srgb,
          var(--primary, #305080) 12%,
          var(--surface, white)
        );
        border-color: color-mix(
          in srgb,
          var(--primary, #305080) 50%,
          var(--outline, #e0e0e0)
        );
      }

      .team-section-expand--assistant {
        color: var(--secondary, #5d6b82);
        border-color: color-mix(
          in srgb,
          var(--secondary, #5d6b82) 35%,
          var(--outline, #e0e0e0)
        );
        background: color-mix(
          in srgb,
          var(--secondary, #5d6b82) 8%,
          var(--surface, white)
        );
      }

      .team-section-expand--assistant:hover {
        background: color-mix(
          in srgb,
          var(--secondary, #5d6b82) 14%,
          var(--surface, white)
        );
        border-color: color-mix(
          in srgb,
          var(--secondary, #5d6b82) 50%,
          var(--outline, #e0e0e0)
        );
      }

      .team-section-expand mat-icon {
        font-size: 22px;
        width: 22px;
        height: 22px;
      }

      .users-search {
        width: 100%;
        margin-bottom: 16px;
      }

      .team-members {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .all-users-directory {
        --directory-col-avatar: 40px;
        --directory-col-stamp: 156px;
        --directory-col-progress: 212px;
        --directory-col-chevron: 24px;

        display: flex;
        flex-direction: column;
        min-width: 0;
        box-sizing: border-box;
        border: 1px solid var(--outline, #e8e8e8);
        border-radius: 10px;
        background-color: var(--surface, white);
      }

      .all-users-directory--limited .all-users-header,
      .all-users-directory--limited .team-member--user {
        grid-template-columns:
          var(--directory-col-avatar)
          minmax(0, 1fr)
          minmax(0, 1fr);
        grid-template-areas: 'avatar identity meta';
      }

      .all-users-viewport {
        flex: none;
        width: 100%;
      }

      .all-users-header,
      .team-member--user {
        display: grid;
        grid-template-columns:
          var(--directory-col-avatar)
          minmax(0, 1fr)
          minmax(0, 1fr)
          var(--directory-col-stamp)
          var(--directory-col-progress)
          var(--directory-col-chevron);
        grid-template-areas: 'avatar identity meta stamp progress chevron';
        column-gap: 16px;
        row-gap: 0;
        align-items: center;
        padding: 8px 12px;
        min-height: 72px;
        box-sizing: border-box;
        min-width: 0;
      }

      .all-users-header {
        position: sticky;
        top: 0;
        z-index: 1;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--onSurfaceVariant, #777);
        background-color: var(--surfaceContainerLow, #f5f5f5);
        border-bottom: 1px solid var(--outline, #e8e8e8);
      }

      .all-users-header-avatar {
        grid-area: avatar;
      }

      .all-users-header-identity {
        grid-area: identity;
      }

      .all-users-header-meta {
        grid-area: meta;
        justify-self: stretch;
        text-align: right;
      }

      .all-users-header-stamp {
        grid-area: stamp;
        width: var(--directory-col-stamp);
        max-width: var(--directory-col-stamp);
      }

      .all-users-header-progress {
        grid-area: progress;
        width: var(--directory-col-progress);
        max-width: var(--directory-col-progress);
      }

      .all-users-header-action {
        grid-area: chevron;
      }

      .all-users-header-sort {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        margin: 0;
        padding: 0;
        border: none;
        background: transparent;
        font: inherit;
        letter-spacing: inherit;
        text-transform: inherit;
        color: inherit;
        cursor: pointer;
        min-width: 0;
      }

      .all-users-header-sort:hover {
        color: var(--primary, #305080);
      }

      .all-users-header-sort .sort-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        opacity: 0.75;
      }

      .all-users-header-meta.all-users-header-sort {
        justify-content: flex-end;
        width: 100%;
      }

      .all-users-header-stamp.all-users-header-sort,
      .all-users-header-progress.all-users-header-sort {
        justify-content: flex-start;
        width: 100%;
      }

      .team-member:not(.team-member--user) {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-radius: 8px;
        background-color: var(--surface, white);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      }

      .team-member--user {
        display: grid;
        flex: none;
        box-sizing: border-box;
        width: auto;
        max-width: none;
        margin: 0;
        border-radius: 0;
        box-shadow: none;
        background-color: transparent;
        border-bottom: 1px solid
          color-mix(in srgb, var(--outline, #e8e8e8) 85%, transparent);
        transition: background-color 0.15s ease;
      }

      .team-member--user:last-child {
        border-bottom: none;
      }

      .team-member--user.clickable {
        cursor: pointer;
      }

      .team-member--user.clickable:hover {
        background-color: color-mix(
          in srgb,
          var(--primary, #305080) 6%,
          var(--surface, white)
        );
      }

      .team-member-details {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        flex: 1;
      }

      .team-member-name {
        font-size: 15px;
        font-weight: 600;
        color: var(--onSurface, #333);
        line-height: 1.3;
      }

      .team-member--user .team-member-details {
        grid-area: identity;
        min-width: 0;
      }

      .team-member--user .team-member-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-role-labels {
        display: flex;
        flex-wrap: nowrap;
        gap: 6px;
        margin-top: 4px;
        overflow: hidden;
      }

      .user-role-label {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        line-height: 1.2;
        white-space: nowrap;
      }

      .user-role-label--sekundaer {
        background: color-mix(in srgb, var(--primary, #305080) 12%, white);
        color: var(--primary, #305080);
        box-shadow: inset 0 0 0 1px
          color-mix(in srgb, var(--primary, #305080) 28%, transparent);
      }

      .user-role-label--uddansvarlig {
        background: var(--primary, #305080);
        color: var(--onPrimary, #fff);
      }

      .user-role-label--uddassistent {
        background: color-mix(in srgb, var(--secondary, #5d6b82) 14%, white);
        color: var(--secondary, #5d6b82);
        box-shadow: inset 0 0 0 1px
          color-mix(in srgb, var(--secondary, #5d6b82) 30%, transparent);
      }

      .team-member-role {
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.02em;
      }

      .team-section--responsible .team-member-role {
        color: var(--primary, #305080);
      }

      .team-section--assistant .team-member-role {
        color: var(--secondary, #5d6b82);
      }

      .team-member--user > app-duna-profile-image {
        grid-area: avatar;
        flex-shrink: 0;
      }

      .user-meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-top: 4px;
      }

      .team-member--user .user-meta {
        grid-area: meta;
        margin-top: 0;
        min-width: 0;
        max-width: 100%;
        justify-self: stretch;
        justify-content: flex-end;
        overflow: hidden;
        padding-right: 2px;
        box-sizing: border-box;
      }

      .team-member--user .user-meta app-duna-educational-path-badge {
        display: block;
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
      }

      .user-stamp {
        grid-area: stamp;
        width: var(--directory-col-stamp);
        min-width: var(--directory-col-stamp);
        max-width: var(--directory-col-stamp);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        overflow: hidden;
        box-sizing: border-box;
        isolation: isolate;
      }

      .user-progress-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 6px;
        min-width: 0;
      }

      .team-member--user .user-progress-row {
        grid-area: progress;
        margin-top: 0;
        width: var(--directory-col-progress);
        min-width: var(--directory-col-progress);
        max-width: var(--directory-col-progress);
        flex-shrink: 0;
        box-sizing: border-box;
        overflow: hidden;
        gap: 8px;
      }

      .team-member--user .user-progress-row .segmented-bar {
        flex: 1 1 auto;
        min-width: 0;
      }

      .team-member--user .user-progress-row .user-percentages {
        flex: 0 0 auto;
      }

      :host ::ng-deep .team-member--user .user-meta .educational-path-badge {
        display: block;
        max-width: 100%;
      }

      :host ::ng-deep .team-member--user .user-meta .path-badges-container {
        justify-content: flex-end;
        flex-wrap: wrap;
        max-width: 100%;
        row-gap: 4px;
      }

      :host ::ng-deep .team-member--user .user-meta .individual-path-badge {
        flex: 0 1 auto;
        max-width: 100%;
      }

      :host ::ng-deep .team-member--user .user-stamp .duna-validation-badge {
        margin-left: 0;
        max-width: 100%;
        box-sizing: border-box;
      }

      .profile-chevron {
        grid-area: chevron;
        color: var(--onSurfaceVariant, #999);
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        font-size: 24px;
        overflow: hidden;
      }

      .user-percentages {
        font-size: 12px;
        font-weight: 600;
        color: var(--onSurfaceVariant, #666);
        white-space: nowrap;
      }

      .segmented-bar {
        display: flex;
        align-items: stretch;
        flex: 1;
        height: 8px;
        border-radius: 4px;
        overflow: hidden;
        background-color: var(--outline, #e0e0e0);
      }

      .segmented-bar .bar-fill {
        height: 100%;
        flex-shrink: 0;
      }

      .segmented-bar .bar-fill.completed {
        background-color: var(--primary, #305080);
      }

      .segmented-bar .bar-fill.intended {
        background-color: var(--secondary, #5d6b82);
      }

      .team-empty {
        margin: 0;
        color: var(--onSurfaceVariant, #777);
        font-size: 14px;
        font-style: italic;
      }

      .error-message {
        color: red;
        margin: 16px;
        padding: 8px;
        border: 1px solid red;
        border-radius: 4px;
      }

      .loading-info {
        margin: 16px;
        padding: 8px;
        text-align: center;
        font-style: italic;
      }

      /* Responsive adjustments */
      @media (max-width: 900px) {
        .all-users-header {
          display: none;
        }

        .team-member--user {
          grid-template-columns: 40px minmax(0, 1fr) 24px;
          grid-template-areas:
            'avatar identity chevron'
            '. meta .'
            '. stamp .'
            '. progress .';
          height: auto;
          min-height: 72px;
          padding: 10px 14px;
          width: 100%;
        }
      }

      @media (max-width: 768px) {
        .location-container {
          margin: 8px;
        }

        .location-header {
          padding: 12px;
        }

        .location-content {
          padding: 12px;
        }

        .education-team {
          grid-template-columns: 1fr;
        }

        .location-title h2 {
          font-size: 18px;
        }

        .team-member--user {
          padding: 10px 12px;
        }
      }
    `,
  ],
})
export class DunaLocationInformationComponent implements OnInit, OnDestroy {
  /** The GUID of the location to display. If not provided, it will be extracted from the URL. */
  @Input() locationGuid: string | null = null;

  /** Whether to use a compact layout with reduced heights */
  @Input() compactMode: boolean = false;

  location: LocationDTO | null = null;
  locationInfo: LocationInformationDTO | null = null;
  profileImageMeta: ProfileImageDTO | null = null;
  error: string | null = null;
  filteredUserRows: LocationUserRow[] = [];
  userSearch = '';
  userSortColumn: DirectorySortColumn = 'identity';
  userSortDirection: DirectorySortDirection = 'asc';
  usersPageIndex = 0;
  usersPageSize = 25;
  usersTotalCount = 0;
  usersListLoading = false;
  readonly usersPageSizeOptions = [10, 25, 50, 100];
  readonly teamMemberCollapseLimit = 5;
  educationResponsiblesExpanded = false;
  educationAssistantsExpanded = false;
  canViewUserDetails = false;
  readonly currentYear = new Date().getFullYear();
  private locationLoadStarted = false;
  private activeLocationGuid: string | null = null;
  private readonly userSearchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    public router: Router,
    private http: HttpClient,
    private profileImageService: ProfileImageService,
    private changeDetector: ChangeDetectorRef,
    private authService: AuthService,
    private userService: UserService,
    private dataSourceService: ComponentDataSourceService
  ) {
  }

  ngOnInit() {
    this.userSearchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.usersPageIndex = 0;
        this.loadLocationUsersPage();
      });
    this.startLocationLoad();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUserInitials(user: UserDTO | null): string {
    if (!user) {
      return '?';
    }
    const first = user.first_name?.charAt(0) ?? '';
    const last = user.last_name?.charAt(0) ?? '';
    const initials = `${first}${last}`.trim();
    return initials || '?';
  }

  onUserSearchChange(value: string): void {
    this.userSearch = value;
    this.userSearchInput$.next(value);
  }

  onUsersPageChange(event: PageEvent): void {
    this.usersPageIndex = event.pageIndex;
    this.usersPageSize = event.pageSize;
    this.loadLocationUsersPage();
  }

  onUserSortHeaderClick(column: DirectorySortColumn, event: Event): void {
    event.stopPropagation();
    if (
      !this.canViewUserDetails &&
      (column === 'stamp' || column === 'progress')
    ) {
      return;
    }
    const next = toggleDirectorySort(
      this.userSortColumn,
      this.userSortDirection,
      column
    );
    this.userSortColumn = next.column;
    this.userSortDirection = next.direction;
    this.usersPageIndex = 0;
    this.loadLocationUsersPage();
  }

  userSortIcon(column: DirectorySortColumn): string {
    if (this.userSortColumn !== column) {
      return 'unfold_more';
    }
    return this.userSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getAllUsersCountLabel(): string {
    return `${this.usersTotalCount}`;
  }

  get visibleEducationResponsibles(): UserDTO[] {
    const list = this.locationInfo?.education_responsibles ?? [];
    if (
      list.length <= this.teamMemberCollapseLimit ||
      this.educationResponsiblesExpanded
    ) {
      return list;
    }
    return list.slice(0, this.teamMemberCollapseLimit);
  }

  get visibleEducationAssistants(): UserDTO[] {
    const list = this.locationInfo?.education_assistants ?? [];
    if (
      list.length <= this.teamMemberCollapseLimit ||
      this.educationAssistantsExpanded
    ) {
      return list;
    }
    return list.slice(0, this.teamMemberCollapseLimit);
  }

  get showEducationResponsiblesExpandToggle(): boolean {
    return (
      (this.locationInfo?.education_responsibles?.length ?? 0) >
      this.teamMemberCollapseLimit
    );
  }

  get showEducationAssistantsExpandToggle(): boolean {
    return (
      (this.locationInfo?.education_assistants?.length ?? 0) >
      this.teamMemberCollapseLimit
    );
  }

  get hiddenEducationResponsiblesCount(): number {
    const total = this.locationInfo?.education_responsibles?.length ?? 0;
    return Math.max(0, total - this.visibleEducationResponsibles.length);
  }

  get hiddenEducationAssistantsCount(): number {
    const total = this.locationInfo?.education_assistants?.length ?? 0;
    return Math.max(0, total - this.visibleEducationAssistants.length);
  }

  toggleEducationResponsiblesExpanded(): void {
    this.educationResponsiblesExpanded = !this.educationResponsiblesExpanded;
    this.changeDetector.markForCheck();
  }

  toggleEducationAssistantsExpanded(): void {
    this.educationAssistantsExpanded = !this.educationAssistantsExpanded;
    this.changeDetector.markForCheck();
  }

  private resetTeamListExpansion(): void {
    this.educationResponsiblesExpanded = false;
    this.educationAssistantsExpanded = false;
  }

  private startLocationLoad(): void {
    if (this.locationLoadStarted) {
      return;
    }
    this.locationLoadStarted = true;
    if (this.locationGuid) {
      this.loadLocationData(this.locationGuid);
    } else {
      this.loadLocationFromUrl();
    }
  }

  private buildUserRow(user: UserDTO): LocationUserRow {
    const progressLabel = this.canViewUserDetails
      ? buildProgressLabelInputsFromStatus(user.status)
      : buildProgressLabelInputsFromStatus(null);
    return {
      user,
      uuid: user.uuid,
      initials: this.getUserInitials(user),
      roleLabels: this.getUserRoleLabels(user),
      educationalPaths: progressLabel.paths,
      validationStatus: this.getValidationStatus(user),
      registeredPercentage: progressLabel.registeredPercentage,
      progressPercentage: progressLabel.progressPercentage,
      completedBarWidth: progressLabel.progressPercentage,
      intendedBarWidth: Math.max(
        0,
        progressLabel.registeredPercentage - progressLabel.progressPercentage
      ),
      progressLabel,
    };
  }

  private loadLocationUsersPage(): void {
    if (!this.activeLocationGuid) {
      return;
    }
    this.usersListLoading = true;

    this.dataSourceService
      .loadDataWithFilters(
        'locations_users',
        buildServerLoadParams(
          'locations_users',
          this.usersPageIndex,
          this.usersPageSize,
          this.userSearch,
          this.userSortColumn,
          this.userSortDirection,
          mapUserListSortToApi,
          [
            {
              column: 'location_guid',
              value: this.activeLocationGuid,
              operator: 'equals',
            },
          ]
        )
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = (result?.items ?? []) as User[];
          this.filteredUserRows = items.map((userModel) =>
            this.buildUserRow(this.userModelToDto(userModel))
          );
          this.usersTotalCount = result?.totalCount ?? items.length;
          this.usersPageIndex = result?.pageIndex ?? this.usersPageIndex;
          this.usersPageSize = result?.pageSize ?? this.usersPageSize;
          this.usersListLoading = false;
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.filteredUserRows = [];
          this.usersTotalCount = 0;
          this.usersListLoading = false;
          this.changeDetector.markForCheck();
        },
      });
  }

  private userModelToDto(user: User): UserDTO {
    return {
      uuid: user.uuid,
      first_name: user.first_name,
      last_name: user.last_name,
      user: user.user,
      roles: user.roles,
      organizations_id: user.organizations_id,
      locations: (user.locations as UserDTO['locations']) ?? null,
      status: user.status,
      id: (user as User & { id?: number }).id ?? null,
      profile_image_obfuscated_name:
        (user as User & { profile_image_obfuscated_name?: string | null })
          .profile_image_obfuscated_name ?? null,
    };
  }

  getUserRoleLabels(
    user: UserDTO
  ): { type: 'sekundaer' | 'uddansvarlig' | 'uddassistent'; label: string }[] {
    const labels: {
      type: 'sekundaer' | 'uddansvarlig' | 'uddassistent';
      label: string;
    }[] = [];
    const isPrimary = this.userHasLocationRole(user, 'primaer');
    const isSecondary = this.userHasLocationRole(user, 'sekundaer');

    if (isSecondary && !isPrimary) {
      labels.push({ type: 'sekundaer', label: 'Sekundær' });
    }

    if (this.userIsEducationResponsible(user)) {
      labels.push({ type: 'uddansvarlig', label: 'Uddannelsesansvarlig' });
    } else if (this.userIsEducationAssistant(user)) {
      labels.push({ type: 'uddassistent', label: 'Uddannelsesassistent' });
    }

    return labels;
  }

  private getUserRoleSearchText(user: UserDTO): string {
    const labels = this.getUserRoleLabels(user);
    const terms: string[] = [];

    if (!labels.some((label) => label.type === 'sekundaer')) {
      terms.push('primær', 'primaer', 'primary');
    }

    for (const label of labels) {
      terms.push(label.label.toLowerCase());
      if (label.type === 'sekundaer') {
        terms.push('sekundær', 'sekundaer', 'sekundar', 'secondary');
      }
      if (label.type === 'uddansvarlig') {
        terms.push('uddannelsesansvarlig', 'uddansvarlig', 'ansvarlig');
      }
      if (label.type === 'uddassistent') {
        terms.push('uddannelsesassistent', 'uddassistent', 'assistent');
      }
    }

    return terms.join(' ');
  }

  private userHasLocationRole(
    user: UserDTO,
    roleKey: 'primaer' | 'sekundaer'
  ): boolean {
    return this.roleValuesMatchLocation(user.roles?.[roleKey]);
  }

  private userIsEducationResponsible(user: UserDTO): boolean {
    if (
      this.locationInfo?.education_responsibles.some(
        (member) => member.uuid === user.uuid
      )
    ) {
      return true;
    }
    return this.roleValuesMatchLocation(user.roles?.['uddansvarlig']);
  }

  private userIsEducationAssistant(user: UserDTO): boolean {
    if (
      this.locationInfo?.education_assistants.some(
        (member) => member.uuid === user.uuid
      )
    ) {
      return true;
    }
    return this.roleValuesMatchLocation(user.roles?.['uddassistent']);
  }

  private roleValuesMatchLocation(values: unknown): boolean {
    const locationNames = this.getLocationNameCandidates();
    if (locationNames.size === 0) {
      return false;
    }
    for (const name of this.toNormalizedNameSet(values)) {
      if (locationNames.has(name)) {
        return true;
      }
    }
    return false;
  }

  private getLocationNameCandidates(): Set<string> {
    return this.toNormalizedNameSet([
      this.location?.clean_name,
      this.location?.locations_name,
      this.locationInfo?.locations_name,
    ]);
  }

  private toNormalizedNameSet(values: unknown): Set<string> {
    const names = new Set<string>();
    const add = (value: unknown) => {
      if (typeof value === 'string' && value.trim()) {
        names.add(value.trim().toLowerCase());
        return;
      }
      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        for (const candidate of [
          record['name'],
          record['clean_name'],
          record['locations_name'],
        ]) {
          if (typeof candidate === 'string' && candidate.trim()) {
            names.add(candidate.trim().toLowerCase());
          }
        }
      }
    };

    if (Array.isArray(values)) {
      for (const value of values) {
        add(value);
      }
      return names;
    }

    add(values);
    return names;
  }

  getValidationStatus(user: UserDTO): DunaValidationStatus | null {
    if (!this.canViewUserDetails) {
      return null;
    }
    const status = user.status?.validation_status as string | undefined;
    if (!status || status === 'hidden') {
      return null;
    }
    if (
      status === 'valid' ||
      status === 'attention' ||
      status === 'no_data' ||
      status === 'no_paths'
    ) {
      return status;
    }
    return 'no_data';
  }

  getUserEducationalPaths(user: UserDTO): any[] {
    return user.status?.educational_paths ?? EMPTY_EDUCATIONAL_PATHS;
  }

  getRegisteredPercentage(user: UserDTO): number {
    if (!this.canViewUserDetails) {
      return 0;
    }
    return this.clampPercent(user.status?.registered_percentage);
  }

  getProgressPercentage(user: UserDTO): number {
    if (!this.canViewUserDetails) {
      return 0;
    }
    return this.clampPercent(user.status?.progress_percentage);
  }

  getCompletedBarWidth(user: UserDTO): number {
    return this.getProgressPercentage(user);
  }

  getIntendedBarWidth(user: UserDTO): number {
    const registered = this.getRegisteredPercentage(user);
    const progress = this.getProgressPercentage(user);
    return Math.max(0, registered - progress);
  }

  onUserCardClick(user: UserDTO): void {
    if (!this.canViewUserDetails || !user.uuid) {
      return;
    }
    this.router.navigate(['/profile', user.uuid]);
  }

  private clampPercent(value: number | undefined | null): number {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return 0;
    }
    return Math.min(100, Math.max(0, Number(value)));
  }

  private async resolveCanViewUserDetails(locationGuid: string): Promise<void> {
    let userLocationGuids: string[] = [];
    try {
      const locations = await firstValueFrom(
        this.userService.getUserLocationsFromApi()
      );
      userLocationGuids = (locations ?? [])
        .map((loc) => loc.guid)
        .filter(Boolean);
    } catch {
      userLocationGuids = [];
    }

    this.canViewUserDetails = userCanViewUserGroupsLocation({
      userRoles: this.authService.getUserRoles(),
      locationGuid,
      userLocationGuids,
    });
    this.changeDetector.detectChanges();
  }

  reloadData() {
    this.error = null;
    this.location = null;
    this.locationInfo = null;
    this.filteredUserRows = [];
    this.usersTotalCount = 0;
    this.activeLocationGuid = null;
    this.locationLoadStarted = false;
    this.startLocationLoad();
  }

  private loadLocationFromUrl() {
    const path = this.router.url;

    // Try to extract GUID directly from URL
    const guidMatch = path.match(/\/lokation\/([a-f0-9-]+)/i);

    if (guidMatch && guidMatch[1]) {
      const locationGuid = guidMatch[1];
      this.loadLocationData(locationGuid);
    } else {
      // Alternative method - split path segments
      const pathSegments = path.split('/');

      const locationIndex = pathSegments.indexOf('lokation');

      if (locationIndex !== -1 && locationIndex < pathSegments.length - 1) {
        const locationGuid = pathSegments[locationIndex + 1];
        this.loadLocationData(locationGuid);
      } else {
        console.error(
          '[DunaLocationInformation] Could not find location GUID in URL'
        );
        this.error = 'No location identifier found in the URL';
        this.changeDetector.detectChanges();
      }
    }
  }

  private loadLocationData(guid: string) {
    this.error = null;

    if (guid === 'test') {
      this.createTestData();
      return;
    }

    // Load location data
    const url = `${environment.apiUrl}/locations/${guid}`;

    this.http.get<LocationDTO>(url).subscribe({
      next: (data) => {
        this.location = data;
        this.createLocationInfo();
        this.loadProfileImageMeta(guid);
        this.activeLocationGuid = guid;
        this.usersPageIndex = 0;
        void this.resolveCanViewUserDetails(guid).then(() => {
          this.loadLocationUsersPage();
        });
        // Automatically scan roles after loading data
        this.scanAllRoles();
        this.changeDetector.detectChanges();
      },
      error: (err) => {
        console.error('[DunaLocationInformation] Error loading location:', err);
        this.error = 'Failed to load location data';
        this.changeDetector.detectChanges();
      },
    });
  }

  private loadProfileImageMeta(guid: string): void {
    this.profileImageService.getLocationProfileImage(guid).subscribe({
      next: (meta) => {
        this.profileImageMeta = meta;
        this.changeDetector.detectChanges();
      },
      error: () => {
        this.profileImageMeta = null;
      },
    });
  }

  getLocationInitials(): string {
    const name =
      this.location?.locations_name ?? this.locationInfo?.locations_name ?? '';
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  onLocationProfileImageChanged(): void {
    const guid = this.location?.guid ?? this.locationInfo?.guid;
    if (guid) {
      this.loadProfileImageMeta(guid);
    }
  }

  private createLocationInfo() {
    if (!this.location) {
      this.error = 'No location data available';
      return;
    }

    try {
      // Get address from the location data
      let address = null;
      if (this.location.data) {
        if (typeof this.location.data === 'object') {
          address =
            this.location.data.locations_address ||
            this.location.data.address ||
            null;
        } else if (typeof this.location.data === 'string') {
          address = this.location.data;
        }
      }

      this.resetTeamListExpansion();

      // Create the location info object
      this.locationInfo = {
        guid: this.location.guid,
        locations_name: this.location.locations_name,
        address: address,
        total_people_count: this.location.users?.length || 0,
        education_responsibles: [],
        education_assistants: [],
      };

      // Process users to find education managers and assistants
      if (this.location.users && this.location.users.length > 0) {
        this.locationInfo.education_responsibles = this.location.users.filter(
          (user) => this.hasRole(user, 'uddansvarlig')
        );

        this.locationInfo.education_assistants = this.location.users.filter(
          (user) => this.hasRole(user, 'uddassistent')
        );
      }

    } catch (error) {
      console.error(
        '[DunaLocationInformation] Error creating location info:',
        error
      );
      this.error = 'Error processing location data';
    }
  }

  scanAllRoles() {
    if (!this.location || !this.location.users) {
      return;
    }

    // Store all unique roles found
    const allRoles = new Set<string>();
    const roleCounts: { [key: string]: number } = {};

    // Loop through all users
    for (const user of this.location.users) {

      if (!user.roles) {
        continue;
      }

      // Check primary roles
      if (user.roles.primaer && Array.isArray(user.roles.primaer)) {
        for (const role of user.roles.primaer) {
          if (typeof role === 'string') {
            allRoles.add(role);
            roleCounts[role] = (roleCounts[role] || 0) + 1;
          } else if (role && typeof role === 'object') {
            continue;
          }
        }
      }

      // Check bruger (user) roles
      if (user.roles.bruger && Array.isArray(user.roles.bruger)) {
        for (const role of user.roles.bruger) {
          if (typeof role === 'string') {
            allRoles.add(role);
            roleCounts[role] = (roleCounts[role] || 0) + 1;
          } else if (role && typeof role === 'object') {
            continue;
          }
        }
      }

      // Check other direct properties
      for (const key in user.roles) {
        if (key !== 'primaer' && key !== 'bruger') {
          if (typeof user.roles[key] === 'string') {
            allRoles.add(user.roles[key] as string);
            roleCounts[user.roles[key] as string] =
              (roleCounts[user.roles[key] as string] || 0) + 1;
          } else if (
            typeof user.roles[key] === 'boolean' &&
            user.roles[key] === true
          ) {
            allRoles.add(key);
            roleCounts[key] = (roleCounts[key] || 0) + 1;
          }
        }
      }
    }

    const relevantRoles = Array.from(allRoles).filter(
      (role) =>
        role.toLowerCase().includes('uddansvarlig') ||
        role.toLowerCase().includes('uddannelsesansvarlig') ||
        role.toLowerCase().includes('uddassistent') ||
        role.toLowerCase().includes('uddannelsesassistent')
    );

    this.updateWithFlexibleRoleDetection(relevantRoles);
  }

  private updateWithFlexibleRoleDetection(relevantRoles: string[]) {
    if (!this.location || !this.location.users || !this.locationInfo) return;

    // Clear existing assignments
    this.locationInfo.education_responsibles = [];
    this.locationInfo.education_assistants = [];

    // Find roles containing the desired patterns
    const managerRolePatterns = relevantRoles.filter((role) =>
      role.toLowerCase().includes('ansvarlig')
    );

    const assistantRolePatterns = relevantRoles.filter((role) =>
      role.toLowerCase().includes('assistent')
    );

    const managersSet = new Set<string>();

    // First pass: identify managers
    for (const user of this.location.users) {
      for (const pattern of managerRolePatterns) {
        if (this.userHasExactRole(user, pattern)) {
          this.locationInfo.education_responsibles.push(user);
          if (user.uuid) {
            managersSet.add(user.uuid);
          }
          break;
        }
      }
    }

    // Second pass: identify assistants (excluding users already identified as managers)
    for (const user of this.location.users) {
      // Skip if user is already a manager
      if (user.uuid && managersSet.has(user.uuid)) {
        continue;
      }

      for (const pattern of assistantRolePatterns) {
        if (this.userHasExactRole(user, pattern)) {
          this.locationInfo.education_assistants.push(user);
          break;
        }
      }
    }

    if (
      this.locationInfo.education_responsibles.length === 0 &&
      this.locationInfo.education_assistants.length === 0
    ) {
      this.tryFlexibleRoleMatching();
    }

    this.changeDetector.detectChanges();
  }

  private userHasExactRole(user: UserDTO, exactRole: string): boolean {
    if (!user || !user.roles) return false;

    // Check primary roles for exact match
    if (user.roles.primaer && Array.isArray(user.roles.primaer)) {
      if (
        user.roles.primaer.some(
          (role) => typeof role === 'string' && role === exactRole
        )
      ) {
        return true;
      }
    }

    // Check bruger roles for exact match
    if (user.roles.bruger && Array.isArray(user.roles.bruger)) {
      if (
        user.roles.bruger.some(
          (role) => typeof role === 'string' && role === exactRole
        )
      ) {
        return true;
      }
    }

    // Check direct properties
    return (
      user.roles[exactRole] === true || user.roles[exactRole] === exactRole
    );
  }

  private tryFlexibleRoleMatching() {
    if (!this.location || !this.location.users || !this.locationInfo) return;

    // First pass: identify managers (uddansvarlig)
    const managers = new Set<string>(); // Store user UUIDs of managers

    for (const user of this.location.users) {
      // Check for any role containing "ansvarlig"
      if (
        this.hasRoleContaining(user, 'ansvarlig') ||
        this.hasRoleContaining(user, 'ansvar')
      ) {
        if (!this.locationInfo.education_responsibles.includes(user)) {
          this.locationInfo.education_responsibles.push(user);
          // Add to the set of managers to exclude from assistants
          if (user.uuid) {
            managers.add(user.uuid);
          }
        }
      }
    }

    // Second pass: identify assistants (uddassistent) excluding managers
    for (const user of this.location.users) {
      // Skip users already identified as managers
      if (user.uuid && managers.has(user.uuid)) {
        continue;
      }

      // Check for any role containing "assistent"
      if (
        this.hasRoleContaining(user, 'assistent') ||
        this.hasRoleContaining(user, 'assist')
      ) {
        if (!this.locationInfo.education_assistants.includes(user)) {
          this.locationInfo.education_assistants.push(user);
        }
      }
    }
  }

  private hasRoleContaining(user: UserDTO, partialRole: string): boolean {
    if (!user || !user.roles) return false;

    // Check primary roles
    if (user.roles.primaer && Array.isArray(user.roles.primaer)) {
      for (const role of user.roles.primaer) {
        if (
          typeof role === 'string' &&
          role.toLowerCase().includes(partialRole.toLowerCase())
        ) {
          return true;
        }
      }
    }

    // Check bruger roles
    if (user.roles.bruger && Array.isArray(user.roles.bruger)) {
      for (const role of user.roles.bruger) {
        if (
          typeof role === 'string' &&
          role.toLowerCase().includes(partialRole.toLowerCase())
        ) {
          return true;
        }
      }
    }

    // Check all properties
    for (const key in user.roles) {
      // Check if the key contains the partial role
      if (key.toLowerCase().includes(partialRole.toLowerCase())) {
        return true;
      }

      // Check if the value contains the partial role
      if (
        typeof user.roles[key] === 'string' &&
        (user.roles[key] as string)
          .toLowerCase()
          .includes(partialRole.toLowerCase())
      ) {
        return true;
      }
    }

    return false;
  }

  private hasRole(user: UserDTO, roleName: string): boolean {
    if (!user || !user.roles) {
      return false;
    }

    const needle = roleName.toLowerCase();

    if (user.roles.primaer && Array.isArray(user.roles.primaer)) {
      for (const role of user.roles.primaer) {
        if (typeof role === 'string' && role.toLowerCase().includes(needle)) {
          return true;
        }

        if (typeof role === 'object' && role !== null) {
          const roleObj = role as Record<string, unknown>;
          if (
            typeof roleObj['name'] === 'string' &&
            roleObj['name'].toLowerCase().includes(needle)
          ) {
            return true;
          }

          for (const key in roleObj) {
            const value = roleObj[key];
            if (typeof value === 'string' && value.toLowerCase().includes(needle)) {
              return true;
            }
          }
        }
      }
    }

    if (user.roles.bruger && Array.isArray(user.roles.bruger)) {
      for (const role of user.roles.bruger) {
        if (typeof role === 'string' && role.toLowerCase().includes(needle)) {
          return true;
        }

        if (typeof role === 'object' && role !== null) {
          const roleObj = role as Record<string, unknown>;
          if (
            typeof roleObj['name'] === 'string' &&
            roleObj['name'].toLowerCase().includes(needle)
          ) {
            return true;
          }

          for (const key in roleObj) {
            const value = roleObj[key];
            if (typeof value === 'string' && value.toLowerCase().includes(needle)) {
              return true;
            }
          }
        }
      }
    }

    for (const key in user.roles) {
      if (key.toLowerCase().includes(needle) && user.roles[key]) {
        return true;
      }

      if (
        typeof user.roles[key] === 'string' &&
        (user.roles[key] as string).toLowerCase().includes(needle)
      ) {
        return true;
      }
    }

    return false;
  }

  private createTestData() {
    // Create test data for troubleshooting
    this.locationInfo = {
      guid: 'test-guid',
      locations_name: 'Test Location',
      address: 'Test Address 123',
      total_people_count: 5,
      education_responsibles: [
        {
          uuid: 'user1',
          first_name: 'John',
          last_name: 'Doe',
          user: 'john.doe@example.com',
          roles: {
            primaer: ['uddansvarlig'],
            bruger: [],
          },
          organizations_id: 1,
          locations: [],
          status: null,
        },
      ],
      education_assistants: [
        {
          uuid: 'user2',
          first_name: 'Jane',
          last_name: 'Smith',
          user: 'jane.smith@example.com',
          roles: {
            primaer: ['uddassistent'],
            bruger: [],
          },
          organizations_id: 1,
          locations: [],
          status: null,
        },
      ],
    };

    this.changeDetector.detectChanges();
  }
}
